import { useState } from 'react'
import { download, query } from '../../api/client'
import { useQuery } from '../../api/hooks'
import type { BalanceSheet, BalanceSheetLine, ReportBasis } from '../../api/types'
import { useAuth } from '../../auth/AuthContext'
import { P } from '../../auth/permissions'
import { Empty, ErrorBanner, Loading, Money, Panel, formatDate, isoDate, money } from '../../components/ui'

/**
 * What the office owns and owes, as at a date.
 *
 * <p>The endpoint has existed since the reports were written and there was no page for it, so the
 * only way to see it was Swagger. It is the one statement that proves the ledger hangs together
 * from the outside: assets against liabilities, equity and this year's result.</p>
 *
 * <p>Reported on the <b>accrual</b> basis by default. A cash-basis balance sheet is a legitimate
 * thing to ask for here — it shows what the position would be with receivables and payables
 * stripped out — but it is not the one anybody signs.</p>
 */
export function BalanceSheetPage() {
  const { can } = useAuth()
  const [asOf, setAsOf] = useState(isoDate(new Date()))
  const [basis, setBasis] = useState<ReportBasis>('Accrual')

  const report = useQuery<BalanceSheet>(
    `/reports/balance-sheet${query({ asOf, basis })}`,
    [asOf, basis],
  )

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>As at</label>
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </div>
        <div className="field">
          <label>Basis</label>
          <select value={basis} onChange={(e) => setBasis(e.target.value as ReportBasis)}>
            <option value="Accrual">Accrual</option>
            <option value="Cash">Cash</option>
          </select>
        </div>

        <div className="spacer" />

        {can(P.reports.export) && (
          <button
            onClick={() =>
              void download(
                `/reports/balance-sheet/excel${query({ asOf, basis })}`,
                `balance-sheet-${asOf}.xlsx`,
              )
            }
          >
            Export to Excel
          </button>
        )}
      </div>

      <ErrorBanner error={report.error} />
      {report.loading && <Loading />}

      {report.data && (
        <>
          {/* Same treatment as the trial balance: an unbalanced statement is not a presentation
              problem, it is a reason to stop and find out why. */}
          {!report.data.isBalanced && (
            <div className="banner error">
              <strong>The balance sheet does not balance.</strong> Assets exceed liabilities, equity
              and the year's result by {money(report.data.difference)}. Check the trial balance
              first — if that balances, an account is in the wrong group.
            </div>
          )}

          <div className="grid cols-2">
            <Section
              title="Assets"
              lines={report.data.assets}
              totalLabel="Total assets"
              total={report.data.totalAssets}
            />

            <div>
              <Section
                title="Liabilities"
                lines={report.data.liabilities}
                totalLabel="Total liabilities"
                total={report.data.totalLiabilities}
              />

              <Section
                title="Equity"
                lines={report.data.equity}
                totalLabel="Total equity"
                total={report.data.totalEquity}
                /* The year's result is not an equity account yet — it is closed at year end — so
                   it is a line of its own rather than folded in, which would make the statement
                   look right for the wrong reason. */
                extra={{
                  label: "Result for the year to date",
                  value: report.data.currentYearResult,
                }}
                grandTotalLabel="Liabilities, equity and result"
                grandTotal={
                  report.data.totalLiabilities +
                  report.data.totalEquity +
                  report.data.currentYearResult
                }
              />
            </div>
          </div>

          <p className="muted" style={{ fontSize: 12 }}>
            As at {formatDate(report.data.asOf)}, {report.data.basis.toLowerCase()} basis. Client
            money held sits in liabilities — it is not the office's money, and the{' '}
            <strong>client funds control</strong> report is what checks it against the cash actually
            held.
          </p>
        </>
      )}
    </>
  )
}

function Section({
  title,
  lines,
  totalLabel,
  total,
  extra,
  grandTotalLabel,
  grandTotal,
}: {
  title: string
  lines: BalanceSheetLine[]
  totalLabel: string
  total: number
  extra?: { label: string; value: number }
  grandTotalLabel?: string
  grandTotal?: number
}) {
  return (
    <Panel title={title} flush>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th style={{ width: 100 }}>Code</th>
              <th>Account</th>
              <th style={{ width: 140 }} className="num">
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr>
                <td colSpan={3}>
                  <Empty>Nothing in this section.</Empty>
                </td>
              </tr>
            )}

            {lines.map((line) => (
              <tr key={line.code} className={line.isTotal ? 'subtotal' : undefined}>
                <td className="code">{line.code}</td>
                <td
                  className={line.isTotal ? 'strong' : undefined}
                  /* The chart is a tree and the report keeps its depth. Indenting is what shows a
                     reader that four accounts roll into the heading above them. */
                  style={{ paddingLeft: 8 + Math.min(Math.max(line.level - 1, 0), 6) * 14 }}
                >
                  {line.nameEn}
                </td>
                <td className={line.isTotal ? 'num strong' : 'num'}>
                  <Money value={line.amount} blankZero />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2}>{totalLabel}</td>
              <td className="num strong">
                <Money value={total} />
              </td>
            </tr>

            {extra && (
              <tr>
                <td colSpan={2}>{extra.label}</td>
                <td className="num">
                  <Money value={extra.value} />
                </td>
              </tr>
            )}

            {grandTotalLabel && (
              <tr>
                <td colSpan={2} className="strong">
                  {grandTotalLabel}
                </td>
                <td className="num strong">
                  <Money value={grandTotal ?? 0} />
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
    </Panel>
  )
}
